/**
 * Контекст для управління станом аутентифікації користувача
 * Відповідає за логін, логаут, реєстрацію, збереження сесії та перевірку токену
 * @context AuthContext
 * @file AuthContext.tsx
 * @path frontend/src/contexts/AuthContext.tsx
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { PUBLIC_ROUTES } from '../constants/routes';
import {
  User as AuthUser,
  UserRole,
  RegisterData,
  LoginData,
  PhoneVerificationData,
  AuthResponse
} from '../types/auth';
import { config } from '../config';
import { useLanguage } from './LanguageContext';
import { translate } from '../locales';

// Інтерфейс контексту аутентифікації
interface AuthContextType {
  // Стан аутентифікації
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Методи аутентифікації
  login: (loginData: LoginData) => Promise<void>;
  register: (registerData: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (userData: Partial<AuthUser>) => void;
  
  // Методи підтвердження
  verifyPhone: (data: PhoneVerificationData) => Promise<void>;
  resendSMSCode: (phone: string) => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  
  // Google OAuth
  loginWithGoogle: () => Promise<void>;
  
  // Перевірки доступу
  hasRole: (roles: UserRole[]) => boolean;
}

// Створення контексту
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Провайдер контексту аутентифікації
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { language } = useLanguage();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Translation helper
  const t = (key: string) => translate(key as any, language as 'en' | 'ua');

  /**
   * Отримує токен з localStorage
   */
  const getToken = (): string | null => {
    return localStorage.getItem(config.auth.tokenKey);
  };

  /**
   * Зберігає токен в localStorage
   */
  const setToken = (token: string): void => {
    // console.log("Saving token to localStorage:", token); // Логування токена перед збереженням
    localStorage.setItem(config.auth.tokenKey, token);
  };

  /**
   * Видаляє токен з localStorage
   */
  const removeToken = (): void => {
    localStorage.removeItem(config.auth.tokenKey);
  };

  /**
   * Виконує API запит з автентифікацією
   */
  const apiCall = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
    const token = getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${config.api.baseUrl}${endpoint}`, {
      ...options,
      headers,
      credentials: 'same-origin',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Network error' }));

      // Handle 403 Forbidden with role information
      if (response.status === 403 && errorData.user_role && errorData.required_roles) {
        const errorMessage = `${errorData.message || 'Доступ заборонено'}\n${t('auth.error.yourRole')}: ${errorData.user_role}\n${t('auth.error.requiredRoles')}: ${errorData.required_roles.join(', ')}`;
        throw new Error(errorMessage);
      }

      throw new Error(errorData.error || 'API Error');
    }

    return response.json();
  };

  /**
   * Логін користувача
   */
  const login = async (loginData: LoginData): Promise<void> => {
    try {
      const response: AuthResponse = await apiCall('/auth/login', {
        method: 'POST',
        body: JSON.stringify(loginData),
      });

      // Визначаємо тип входу
      const loginType: 'email' | 'phone' = loginData.email ? 'email' : 'phone';
      const userWithLoginType = {
        ...response.user,
        login_type: loginType
      };

      setUser(userWithLoginType);
      setToken(response.token);
      localStorage.setItem(config.auth.userKey, JSON.stringify(userWithLoginType));

      // console.log('🔐 Користувач успішно залогінений:', {
      //   email: response.user.email,
      //   phone: response.user.phone,
      //   role: response.user.role,
      //   loginType: loginType
      // });
    } catch (error) {
      console.error('❌ Помилка входу:', error);
      throw error;
    }
  };

  /**
   * Реєстрація користувача
   */
  const register = async (registerData: RegisterData): Promise<void> => {
    try {
      const response: AuthResponse = await apiCall('/auth/register', {
        method: 'POST',
        body: JSON.stringify(registerData),
      });

      setUser(response.user);
      setToken(response.token);
      localStorage.setItem(config.auth.userKey, JSON.stringify(response.user));

      console.log('� Користувач успішно зареєстрований:', {
        email: response.user.email,
        phone: response.user.phone,
        role: response.user.role
      });
    } catch (error) {
      console.error('❌ Помилка реєстрації:', error);
      throw error;
    }
  };

  /**
   * Підтвердження телефону
   */
  const verifyPhone = async (data: PhoneVerificationData): Promise<void> => {
    try {
      await apiCall('/api/v1/auth/verify-phone', {
        method: 'POST',
        body: JSON.stringify(data),
      });

      // Оновлюємо статус підтвердження телефону
      if (user) {
        const updatedUser = { ...user, phone_verified: true };
        setUser(updatedUser);
        localStorage.setItem(config.auth.userKey, JSON.stringify(updatedUser));
      }

      console.log('📱 Телефон успішно підтверджений');
    } catch (error) {
      console.error('❌ Помилка підтвердження телефону:', error);
      throw error;
    }
  };

  /**
   * Повторне надсилання SMS коду
   */
  const resendSMSCode = async (phone: string): Promise<void> => {
    try {
      await apiCall(`/api/v1/auth/resend-sms/${encodeURIComponent(phone)}`, {
        method: 'POST',
      });

      console.log('📨 SMS код надіслано повторно');
    } catch (error) {
      console.error('❌ Помилка надсилання SMS:', error);
      throw error;
    }
  };

  /**
   * Підтвердження email
   */
  const verifyEmail = async (token: string): Promise<void> => {
    try {
      await apiCall('/api/v1/auth/verify-email', {
        method: 'POST',
        body: JSON.stringify({ token }),
      });

      // Оновлюємо статус підтвердження email
      if (user) {
        const updatedUser = { ...user, is_verified: true };
        setUser(updatedUser);
        localStorage.setItem(config.auth.userKey, JSON.stringify(updatedUser));
      }

      console.log('📧 Email успішно підтверджений');
    } catch (error) {
      console.error('❌ Помилка підтвердження email:', error);
      throw error;
    }
  };

  /**
   * Google OAuth логін
   */
  const loginWithGoogle = async (): Promise<void> => {
    try {
      // Отримуємо URL для Google авторизації
      const response = await apiCall('/auth/google/login');

      // Перенаправляємо користувача на Google
      window.location.href = response.auth_url;
    } catch (error) {
      console.error('❌ Помилка Google авторизації:', error);
      throw error;
    }
  };

  /**
   * Відновлює сесію користувача при завантаженні додатку
   */
  const restoreSession = async (): Promise<void> => {
    try {
      // Перевіряємо чи є токен в URL параметрах (від OAuth redirect)
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get('token');
      const urlError = urlParams.get('error');

      if (urlToken) {
        // Якщо є токен в URL, використовуємо його для аутентифікації
        console.log('🔑 Знайдено токен в URL від OAuth redirect');
        setToken(urlToken);

        // Отримуємо інформацію про користувача
        const response = await fetch(`${config.api.baseUrl}/auth/me`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${urlToken}`,
          },
          credentials: 'same-origin',
        });

        if (response.ok) {
          const data = await response.json();
          const userData = data.user || data;

          setUser(userData);
          localStorage.setItem(config.auth.userKey, JSON.stringify(userData));

          console.log('✅ Користувач успішно авторизований через OAuth:', {
            email: userData.email,
            role: userData.role
          });

          // Очищуємо URL від токена
          const newUrl = window.location.pathname + window.location.hash;
          window.history.replaceState({}, document.title, newUrl);
        } else {
          console.error('❌ Токен з URL невалідний');
          removeToken();
        }
      } else if (urlError) {
        console.error('❌ Помилка OAuth:', urlError);
        // Очищуємо URL від помилки
        const newUrl = window.location.pathname + window.location.hash;
        window.history.replaceState({}, document.title, newUrl);
      } else {
        // Звичайне відновлення сесії
        const savedUser = localStorage.getItem(config.auth.userKey);
        const token = getToken();
        if (savedUser && token) {
          // Спочатку встановлюємо збережені дані
          const parsedSavedUser = JSON.parse(savedUser);
          setUser(parsedSavedUser);

          // Реальна перевірка токена через бекенд
          const response = await fetch(`${config.api.baseUrl}/auth/me`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            credentials: 'same-origin',
          });
          if (response.ok) {
            const data = await response.json();
            // Handle both {user: {...}} and direct user object responses
            const userData = data.user || data;

            // Зливаємо дані: використовуємо дані з API, але зберігаємо роль з localStorage якщо API не повертає її
            const mergedUser = {
              ...parsedSavedUser,
              ...userData,
              role: userData.role || parsedSavedUser.role // Перевага API даним, але fallback на localStorage
            };

            setUser(mergedUser);
            // Оновлюємо localStorage з новими даними
            localStorage.setItem(config.auth.userKey, JSON.stringify(mergedUser));

            console.log('🔄 Сесія користувача відновлена:', {
              email: mergedUser.email,
              phone: mergedUser.phone,
              role: mergedUser.role,
              source: userData.role ? 'API' : 'localStorage'
            });
          } else {
            console.warn('❌ Токен невалідний, видаляємо сесію');
            removeToken();
            localStorage.removeItem(config.auth.userKey);
          }
        }
      }
    } catch (error) {
      console.error('❌ Помилка відновлення сесії:', error);
      localStorage.removeItem(config.auth.userKey);
      removeToken();
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Логаут користувача
   */
  const logout = async (): Promise<void> => {
    try {
      await apiCall('/api/v1/auth/logout', {
        method: 'POST',
      });

      console.log('✅ Logout успішний');
    } catch (error) {
      console.error('⚠️ Помилка при logout на сервері:', error);
    } finally {
      setUser(null);
      removeToken();
      localStorage.removeItem(config.auth.userKey);
      console.log('🔓 Користувач вийшов з системи');
      window.location.href = PUBLIC_ROUTES.HOME;
    }
  };

  /**
   * Оновлення даних користувача
   */
  const updateUser = (userData: Partial<AuthUser>): void => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem(config.auth.userKey, JSON.stringify(updatedUser));
      console.log('📝 Дані користувача оновлено:', userData);
    }
  };

  /**
   * Перевіряє, чи має користувач одну з вказаних ролей
   */
  const hasRole = (roles: UserRole[]): boolean => {
    if (!user || roles.length === 0) return false;
    return roles.includes(user.role);
  };

  // Ефект для відновлення сесії при завантаженні додатку
  useEffect(() => {
    restoreSession();
  }, []);

  // Значення контексту
  const value: AuthContextType = {
    user,
    isAuthenticated: !!user && !!getToken(),
    isLoading,
    login,
    register,
    logout,
    updateUser,
    verifyPhone,
    resendSMSCode,
    verifyEmail,
    loginWithGoogle,
    hasRole
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Хук для використання контексту аутентифікації
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

/**
 * Хук для перевірки конкретної ролі користувача
 */
export const useRole = (role: UserRole) => {
  const { user, hasRole } = useAuth();
  
  return {
    hasRole: hasRole([role]),
    user
  };
};
